<template>
  <div>
    <div class="flex-container">
      <div class="flex-item iq-card" style="width: 100%;">
        <div class="iq-card-header pt-2">
          <div class="iq-card-title">
            <h5 class="card-title">Main Engine Summary</h5>
          </div>
        </div>

        <div class="iq-card-body">
          <template v-if="hasRpm">
            <div class="px-3 pb-2">
              <p class="mb-0">
                <b>RPM Sensors:</b> {{ currAssetDeviceStatus.rpmTypes.length }}
                ({{ rpmDisplayLabels.join(', ') }})
              </p>
            </div>
            <div class="flex-container">
              <table
                class="table flex-item"
                v-for="type in currAssetDeviceStatus.rpmTypes"
                :key="type"
              >
                <thead class="thead-light">
                  <th>{{ capitalize(rpmLabel(type)) }} RPM Summary</th>
                  <th>Value</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Average RPM</b></td>
                    <td>{{ rpmStat(type).average }}</td>
                    <td>RPM</td>
                  </tr>
                  <tr>
                    <td><b>Engine Running Hour</b></td>
                    <td>{{ rpmStat(type).runningTime }}</td>
                    <td>Hour</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
          <div
            class="flex-container"
            v-if="currAssetDeviceStatus.hasExhaustTemperature && exhaustRows.length"
          >
            <table class="table flex-item">
              <thead class="thead-light">
                <th>Exhaust Temperature Summary</th>
                <th>Value</th>
                <th>Unit</th>
              </thead>
              <tbody>
                <tr v-for="row in exhaustRows" :key="row.key">
                  <td><b>Avg {{ row.label }} Exhaust Temperature</b></td>
                  <td>{{ row.value }}</td>
                  <td>°C</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="!hasRpm && !exhaustRows.length" class="no-device-note">No RPM device</div>
        </div>
      </div>
    </div>

    <div class="flex-container" v-if="currAssetDeviceStatus.aeCount > 0">
      <div class="flex-item iq-card" style="width: 100%;">
        <div class="iq-card-header pt-2">
          <div class="iq-card-title">
            <h5 class="card-title">Auxiliary Engine Summary</h5>
          </div>
        </div>
        <div class="iq-card-body">
          <table class="table auto-width-table">
            <thead class="thead-light">
              <th>Channel</th>
              <th>Engine</th>
              <th>Running Hour</th>
              <th v-if="hasAeFlowmeter">Fuel Cons By Flowmeter</th>
              <th v-if="!isAeConsHidden">Fuel Cons By Constanta</th>
            </thead>
            <tbody>
              <template v-for="channel in aeFuelChannels">
                <tr v-for="(aeNum, idx) in channel.aeNums" :key="`${channel.groupName}-${aeNum}`">
                  <td v-if="idx === 0" :rowspan="channel.aeNums.length">
                    <b>{{ channel.groupName ? prettyName(channel.groupName) : '—' }}</b>
                  </td>
                  <td>AE {{ aeNum }}</td>
                  <td>{{ aeStat(aeNum).runningSeconds }}</td>
                  <td v-if="hasAeFlowmeter && idx === 0" :rowspan="channel.aeNums.length">
                    {{ channel.groupName ? `${channel.totalVolume} Liter` : '—' }}
                  </td>
                  <td v-if="!isAeConsHidden">{{ aeStat(aeNum).fuelConsumption }} Liter</td>
                </tr>
              </template>
              <tr>
                <td colspan="2"><b>Total</b></td>
                <td><b>{{ currAssetSummary.ae && currAssetSummary.ae.runningSeconds }}</b></td>
                <td v-if="hasAeFlowmeter"><b>{{ totalAeFuelByFlowmeter }} Liter</b></td>
                <td v-if="!isAeConsHidden"><b>{{ currAssetSummary.ae && currAssetSummary.ae.fuelConsumption }} Liter</b></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="flex-container" v-if="flowmeterGroups.length > 0">
      <div class="flex-item iq-card" style="width: 70%;">
        <div class="iq-card-header pt-2">
          <div class="iq-card-title">
            <h5 class="card-title">Flowmeter Fuel Summary</h5>
          </div>
        </div>

        <div class="iq-card-body">
          <div>
            <div v-for="group in flowmeterGroups" :key="group.name" class="flex-item" style="width: 100%">
              <table v-if="group.hasIn && group.hasOut" class="table">
                <thead class="thead-light">
                  <th>{{ prettyName(group.name) }} Fuel Summary</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Net</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Avg Flow</b></td>
                    <td>{{ groupData(group.name).in && groupData(group.name).in.avgFuelFlow }}</td>
                    <td>{{ groupData(group.name).out && groupData(group.name).out.avgFuelFlow }}</td>
                    <td><b>{{ groupNetAvgFlow(group.name) }}</b></td>
                    <td>L/h</td>
                  </tr>
                  <tr>
                    <td><b>Density</b></td>
                    <td>{{ groupData(group.name).in && groupData(group.name).in.avgDensity }}</td>
                    <td>{{ groupData(group.name).out && groupData(group.name).out.avgDensity }}</td>
                    <td>-</td>
                    <td>g/cm&sup3;</td>
                  </tr>
                  <tr>
                    <td><b>Volume</b></td>
                    <td>{{ groupData(group.name).in && groupData(group.name).in.engineCons }}</td>
                    <td>{{ groupData(group.name).out && groupData(group.name).out.engineCons }}</td>
                    <td><b>{{ groupNetVolume(group.name) }}</b></td>
                    <td>Liter</td>
                  </tr>
                </tbody>
              </table>
              <table v-else class="table">
                <thead class="thead-light">
                  <th>{{ prettyName(group.name) }} Fuel Summary</th>
                  <th>Value</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Avg Flow</b></td>
                    <td>{{ groupNetAvgFlow(group.name) }}</td>
                    <td>L/h</td>
                  </tr>
                  <tr>
                    <td><b>Density</b></td>
                    <td>{{ groupData(group.name).in && groupData(group.name).in.avgDensity }}</td>
                    <td>g/cm&sup3;</td>
                  </tr>
                  <tr>
                    <td><b>Total Volume</b></td>
                    <td>{{ groupNetVolume(group.name) }}</td>
                    <td>Liter</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="flex-container" v-if="hasReservoirTank">
            <table class="table flex-item">
              <thead class="thead-light">
                <th>Reservoir Tank Summary</th>
                <th>Value</th>
                <th>Unit</th>
              </thead>
              <tbody>
                <tr v-for="row in tankRows" :key="row.key">
                  <td><b>Average {{ row.label }} Reservoir Temperature</b></td>
                  <td>{{ row.value }}</td>
                  <td>°C</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <fuel-consumption-summary-card
        :groups="flowmeterGroups"
        :fm-summary="rawSummary?.flowmeter?.summary"
        :ae-cons="currAssetSummary?.ae?.fuelConsumption"
        :ae-hidden="isAeConsHidden" />
    </div>
  </div>
</template>

<script>
import { getRpmTypeLabel, buildAeFuelChannels } from '@/util/ship/fms-summary';
import { prettyLabel } from '@/util/ship/vesselDetail';
import { formatNum, parseNum } from '@/util/number';
import FuelConsumptionSummaryCard from '@/components/report/FuelConsumptionSummaryCard';

export default {
  name: 'AeFuelSummaryRedesign',
  components: { FuelConsumptionSummaryCard },
  props: {
    currAsset: {
      type: Object,
      default: null,
    },
    currAssetSummary: {
      type: Object,
      required: true,
    },
    rawSummary: {
      type: Object,
      default: null,
    },
    currAssetDeviceStatus: {
      type: Object,
      required: true,
    },
    isAeConsHidden: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasRpm() {
      return (this.currAssetDeviceStatus?.rpmTypes || []).length > 0;
    },
    flowmeterGroups() {
      return this.currAssetDeviceStatus?.flowmeterGroups || [];
    },
    rpmDisplayLabels() {
      const rpmTypes = this.currAssetDeviceStatus?.rpmTypes || [];
      return rpmTypes.map((type) => getRpmTypeLabel(type, rpmTypes));
    },
    exhaustRows() {
      const ex = this.currAssetSummary?.temperature?.exhaust || {};
      return ['port', 'starboard', 'center']
        .filter((pos) => ex[pos] && ex[pos].average !== '-' && ex[pos].average !== undefined)
        .map((pos) => ({ key: pos, label: this.posLabel(pos), value: ex[pos].average }));
    },
    tankRows() {
      const tank = this.currAssetSummary?.temperature?.tank || {};
      return ['port', 'starboard', 'center']
        .filter((pos) => tank[pos] && tank[pos].average !== '-' && tank[pos].average !== undefined)
        .map((pos) => ({ key: pos, label: this.posLabel(pos), value: tank[pos].average }));
    },
    hasReservoirTank() {
      const hasDevice = (this.currAsset?.devices || []).some(
        (d) => d && d.devcLabel && d.devcLabel.toLowerCase() === 'temperature',
      );
      return hasDevice && this.tankRows.length > 0;
    },
    aeFuelChannels() {
      // Channel flowmeter per-AE (Fuel Cons By Flowmeter), dikelompokin biar
      // AE yang satu line (mis. AE1-3 Inline) share satu angka flowmeter.
      const aeCount = this.currAssetDeviceStatus?.aeCount || 0;
      return buildAeFuelChannels(aeCount, this.flowmeterGroups).map((channel) => ({
        ...channel,
        avgFuelFlow: channel.groupName ? this.groupNetAvgFlow(channel.groupName) : null,
        totalVolume: channel.groupName ? this.groupNetVolume(channel.groupName) : null,
      }));
    },
    hasAeFlowmeter() {
      return this.aeFuelChannels.some((channel) => channel.groupName);
    },
    totalAeFuelByFlowmeter() {
      // Total Fuel Cons By Flowmeter khusus channel AE (bukan total semua flowmeter/ME).
      const total = this.aeFuelChannels.reduce((sum, channel) => sum + (parseNum(channel.totalVolume) || 0), 0);
      return formatNum(total, 2);
    },
  },
  methods: {
    capitalize(s) {
      if (typeof s !== 'string') return '';
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    rpmLabel(type) {
      return getRpmTypeLabel(type, this.currAssetDeviceStatus?.rpmTypes || []);
    },
    rpmStat(type) {
      return this.currAssetSummary?.rpm?.[type] || {};
    },
    posLabel(pos) {
      const upper = String(pos).toUpperCase();
      return this.capitalize(getRpmTypeLabel(upper, this.currAssetDeviceStatus?.rpmTypes || []));
    },
    prettyName(name) {
      return prettyLabel(name);
    },
    groupData(name) {
      return this.currAssetSummary?.fm?.[name] || {};
    },
    groupNetAvgFlow(name) {
      const data = this.groupData(name);
      const inVal = parseNum((data.in && data.in.avgFuelFlow) || 0) || 0;
      const outVal = parseNum((data.out && data.out.avgFuelFlow) || 0) || 0;
      return formatNum((inVal - outVal), 2);
    },
    groupNetVolume(name) {
      const data = this.groupData(name);
      const inVal = parseNum((data.in && data.in.engineCons) || 0) || 0;
      const outVal = parseNum((data.out && data.out.engineCons) || 0) || 0;
      return formatNum((inVal - outVal), 2);
    },
    aeStat(i) {
      const ae = (this.currAssetSummary && this.currAssetSummary.ae) || {};
      return ae[`ae${i}`] || {};
    },
  },
};
</script>

<style scoped>
.flex-container {
  display: flex;
  justify-content: space-between;
  margin: 0px 10px;
}

.flex-item {
  margin: 10px 5px;
}

.table {
  width: 100%;
  table-layout: fixed;
}

.table th,
.table td {
  width: 33.33%;
  padding: 8px;
}

.auto-width-table th,
.auto-width-table td {
  width: auto;
}

.group-header-row td {
  background: #f5f7fb;
  padding-top: 10px;
}

.no-device-note {
  padding: 24px 16px;
  color: #6b7280;
  font-style: italic;
  text-align: center;
}

@media screen and (max-width: 991px) {
  .flex-container {
    flex-direction: column;
  }

  .flex-item {
    width: 100% !important;
    margin: 10px 0px !important;
  }
}

@media screen and (max-width: 575px) {
  .iq-card-body {
    padding-left: 12px;
    padding-right: 12px;
  }
}
</style>
